import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, ExternalLink, Trash2, Loader2, FileText, Eye, EyeOff } from "lucide-react";
import { adminGet, adminDelete } from "../../lib/api";

const PagesManager = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    try {
      setLoading(true);
      setPages(await adminGet("/pages"));
    } catch (e) {
      toast.error("Failed to load pages");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try {
      await adminDelete(`/pages/${p.id}`);
      toast.success("Page deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid="admin-pages-page" className="space-y-8">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-display text-[11px] uppercase tracking-[0.3em] text-[#0A192F]/60">Content</div>
          <h1 className="mt-2 font-display font-black text-4xl text-[#0A192F] tracking-tighter">Pages</h1>
          <p className="mt-3 text-[#0A192F]/70 max-w-2xl text-sm">
            Custom pages you add here get published at <code className="bg-gray-100 px-1.5 py-0.5 text-xs">/pages/&lt;slug&gt;</code> and can be linked from the navigation.
          </p>
        </div>
        <button
          onClick={() => navigate("/admin/pages/new")}
          data-testid="page-add-btn"
          className="inline-flex items-center gap-2 bg-[#0A192F] text-white px-5 py-3 font-display text-xs uppercase tracking-[0.25em] font-bold hover:bg-[#D4AF37] hover:text-[#0A192F] transition-colors"
        >
          <Plus size={14} /> Add New Page
        </button>
      </header>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-[#0A192F]/40">
          <Loader2 className="animate-spin mr-2" size={18} /> Loading...
        </div>
      ) : pages.length === 0 ? (
        <div className="bg-white border border-dashed border-gray-300 p-12 text-center" data-testid="pages-empty">
          <FileText className="mx-auto text-[#0A192F]/30" size={40} />
          <p className="mt-4 text-[#0A192F]/70">No custom pages yet. Click <strong>Add New Page</strong> to create your first one.</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm" data-testid="pages-table">
            <thead className="bg-[#F0F4F8] text-[10px] font-display uppercase tracking-wider text-[#0A192F]/60">
              <tr>
                <th className="text-left px-4 py-3">Title</th>
                <th className="text-left px-4 py-3">Slug</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Updated</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pages.map((p) => (
                <tr key={p.id} className="border-t border-gray-100 hover:bg-[#F9FAFB]" data-testid={`page-row-${p.slug}`}>
                  <td className="px-4 py-3">
                    <Link to={`/admin/pages/${p.id}`} className="font-semibold text-[#0A192F] hover:text-[#D4AF37]">
                      {p.title}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-[#0A192F]/70 font-mono text-xs">/pages/{p.slug}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-xs font-display uppercase tracking-wider font-bold ${p.published ? "text-green-700" : "text-gray-500"}`}>
                      {p.published ? <><Eye size={12} /> Published</> : <><EyeOff size={12} /> Draft</>}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[#0A192F]/70 text-xs">
                    {p.updated_at ? new Date(p.updated_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      {p.published && (
                        <a
                          href={`/pages/${p.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`page-view-${p.slug}`}
                          title="View live"
                          className="p-2 border border-gray-300 text-[#0A192F] hover:border-[#D4AF37] hover:text-[#D4AF37]"
                        >
                          <ExternalLink size={14} />
                        </a>
                      )}
                      <button
                        onClick={() => remove(p)}
                        data-testid={`page-delete-${p.slug}`}
                        title="Delete"
                        className="p-2 border border-red-200 text-red-600 hover:bg-red-50"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PagesManager;
